import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabaseClient";

// ── Constants ─────────────────────────────────────────────────────────────────

const GROUP_ROLES = [
  "Account Manager","Accounts Payable Officer","Accounts Payable Supervisor",
  "Accounts Receivable Clerk","Area Training & Compliance Manager","Assistant Accountant",
  "Assistant Branch Manager","Assistant Marketing Manager","Branch Manager",
  "Branch Team Member","Business Analyst","Business Development Manager","CEO",
  "Chief Financial Officer","Chief Marketing Officer","Commercial Analyst",
  "Credit Manager","Credit Officer","Director","Distribution Manager",
  "Financial Controller","Inventory Controller","Key Account Manager",
  "Logistics & Supply Chain Manager","Member Support Team Member",
  "National Operations Manager","National Sales Manager","Network Support Team Member",
  "NSW Operations Manager","Payroll","People & Culture Manager","Pricing Analyst",
  "Principal Software Engineer","Projects Coordinator","Procurement & Inventory Officer",
  "Procurement & Supply Chain Manager","Product Manager","Project & Estimating Manager",
  "Purchasing Officer","Senior Financial Accountant",
];

const GROUP_LOCATIONS = [
  "102 Marketing NSW","103 Supply Chain & Procurement NSW","104 HO Operations NSW",
  "104 HO Sales NSW","105 Information Technology NSW","106 Board & Committees VIC",
  "106 Broad & Committees NSW","107 CEO & Property NSW","108 Finance NSW",
  "109 Team & Culture NSW","201 Leichhardt","202 Blacktown","203 Artarmon",
  "204 Liverpool","205 Brookvale","206 Caringbah","207 Rosebery","210 Broadmeadow",
  "212 Campbelltown","215 Waverley","217 Wollongong","218 Penrith","220 Padstow",
  "221 Silverwater","223 Mittagong","225 Castle Hill","226 Katoomba","227 Matraville",
  "230 Erina","231 Nowra","280 NSW Operations","280 NSW Sales","298 Distribution Centre","308 Cheltenham",
  "309 Mitcham","316 Thomastown","319 Craigieburn","329 Hoppers Crossing",
  "380 VIC Operations","380 VIC Sales","440 Burleigh Heads","441 Acacia Ridge",
  "480 QLD Operations","480 QLD Sales",
];

const ALL_GROUPS = [...GROUP_ROLES, ...GROUP_LOCATIONS].sort();

const ROLE_LABELS = { learner: "Learner", manager: "Manager", superadmin: "Super Admin" };
const ROLE_COLORS = {
  learner: "bg-gray-100 text-gray-600",
  manager: "bg-blue-100 text-blue-700",
  superadmin: "bg-purple-100 text-purple-700",
};

const EMPTY_FORM = {
  firstName: "", lastName: "", email: "", password: "",
  role: "", groupRole: "", groupLocation: "",
};

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium ${
      toast.type === "success" ? "bg-gray-900 text-white" :
      toast.type === "warning" ? "bg-amber-500 text-white" :
      "bg-red-600 text-white"
    }`}>
      {toast.message}
    </div>
  );
}

// ── User Side Panel ───────────────────────────────────────────────────────────

function UserPanel({ user, onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("learner");
  const [active, setActive] = useState(true);
  const [groups, setGroups] = useState([]);
  const [groupSearch, setGroupSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState(null);

  function showToast(message, type = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }

  useEffect(() => {
    if (user) {
      setEmail(user.email || "");
      setRole(user.role || "learner");
      setActive(user.active !== false);
      setGroups(Array.isArray(user.groups) ? [...user.groups] : []);
      setDirty(false);
      setGroupSearch("");
    }
  }, [user]);

  function toggleGroup(g) {
    setGroups(prev =>
      prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]
    );
    setDirty(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ email, role, active, groups })
        .eq("user_id", user.user_id);

      if (error) throw error;
      showToast(`${user.first_name} ${user.last_name} updated.`);
      setDirty(false);
      onSaved({ ...user, email, role, active, groups });
    } catch (err) {
      showToast(err.message || "Failed to save.", "error");
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;

  const filteredGroups = ALL_GROUPS.filter(g =>
    g.toLowerCase().includes(groupSearch.toLowerCase())
  );
  const assignedFiltered = filteredGroups.filter(g => groups.includes(g));
  const availableFiltered = filteredGroups.filter(g => !groups.includes(g));

  return (
    <>
      <Toast toast={toast} />
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/20 z-30" onClick={onClose} />
      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-[420px] bg-white shadow-2xl z-40 flex flex-col">

        {/* Header — fixed, never shrinks */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {user.first_name} {user.last_name}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">{user.user_id}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — calc-based max-height guarantees footer is always visible */}
        <div
          className="overflow-y-auto px-6 py-5 space-y-6"
          style={{ maxHeight: "calc(100vh - 140px)" }}
        >
          {/* Email */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setDirty(true); }}
              className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800"
            />
          </div>

          {/* Role */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</label>
            <div className="flex gap-2">
              {["learner", "manager", "superadmin"].map(r => (
                <button
                  key={r}
                  onClick={() => { setRole(r); setDirty(true); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    role === r
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {ROLE_LABELS[r]}
                </button>
              ))}
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account Status</p>
              <p className="text-sm text-gray-600 mt-0.5">{active ? "Active" : "Disabled"}</p>
            </div>
            <button
              onClick={() => { setActive(prev => !prev); setDirty(true); }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                active ? "bg-gray-900" : "bg-gray-200"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                active ? "translate-x-6" : "translate-x-1"
              }`} />
            </button>
          </div>

          {/* Groups */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              Groups ({groups.length} assigned)
            </label>
            <input
              type="text"
              placeholder="Search groups..."
              value={groupSearch}
              onChange={e => setGroupSearch(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800"
            />

            <div className="max-h-48 overflow-y-auto space-y-1 border border-gray-100 rounded-lg p-2">
              {/* Assigned */}
              {assignedFiltered.map(g => (
                <button
                  key={g}
                  onClick={() => toggleGroup(g)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-medium hover:bg-gray-700 transition-colors"
                >
                  <span className="truncate text-left">{g}</span>
                  <svg className="w-3.5 h-3.5 ml-2 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              ))}
              {/* Available */}
              {availableFiltered.map(g => (
                <button
                  key={g}
                  onClick={() => toggleGroup(g)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 text-gray-600 text-xs font-medium hover:border-gray-900 hover:bg-gray-50 transition-colors group"
                >
                  <span className="truncate text-left">{g}</span>
                  <svg className="w-3.5 h-3.5 ml-2 flex-shrink-0 text-gray-300 group-hover:text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              ))}
              {filteredGroups.length === 0 && (
                <p className="text-center text-xs text-gray-400 py-4">No groups match</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer — always visible */}
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="w-full py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-40 transition-colors"
          >
            {saving ? "Saving..." : dirty ? "Save Changes" : "Saved"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function UserManagement() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setStatus(null);
  };

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("user_id, first_name, last_name, email, role, groups, active")
      .order("last_name", { ascending: true });
    if (!error) setUsers(data || []);
    setUsersLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    try {
      const { data, error } = await supabase.functions.invoke("create-user", {
        body: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          password: form.password,
          role: form.role,
          groupRole: form.groupRole,
          groupLocation: form.groupLocation,
        },
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      setStatus({ type: "success", message: `✓ ${form.firstName} ${form.lastName} created successfully.` });
      setForm(EMPTY_FORM);
      loadUsers();
    } catch (err) {
      setStatus({ type: "error", message: `✗ ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  const isValid = form.firstName && form.lastName && form.email &&
    form.password && form.role && form.groupRole && form.groupLocation;

  const filteredUsers = users.filter(u => {
    const str = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase();
    return str.includes(search.toLowerCase());
  });

  function handleUserSaved(updated) {
    setUsers(prev => prev.map(u => u.user_id === updated.user_id ? updated : u));
    setSelectedUser(updated);
  }

  return (
    <div className="p-6 space-y-8">
      <Toast toast={null} />

      <h1 className="text-2xl font-bold text-gray-800">User Management</h1>

      {/* ── Create New User ── */}
      <form
        onSubmit={handleSubmit}
        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-5"
      >
        <h3 className="text-base font-semibold text-gray-800">Create New User</h3>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">First Name</label>
            <input type="text" name="firstName" value={form.firstName} onChange={handleChange}
              placeholder="e.g. Sarah"
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400" required />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Last Name</label>
            <input type="text" name="lastName" value={form.lastName} onChange={handleChange}
              placeholder="e.g. Jones"
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400" required />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Email Address</label>
            <input type="email" name="email" value={form.email} onChange={handleChange}
              placeholder="e.g. sarah@plumberscoop.com.au"
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400" required />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Password</label>
            <input type="password" name="password" value={form.password} onChange={handleChange}
              placeholder="Minimum 6 characters"
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
              required minLength={6} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">System Role</label>
            <select name="role" value={form.role} onChange={handleChange}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400" required>
              <option value="">Select a role…</option>
              <option value="learner">Learner</option>
              <option value="manager">Manager</option>
              <option value="superadmin">Super Admin</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400 uppercase tracking-wide">Tenant ID (auto-assigned)</label>
            <input type="text" value="b890085b-6358-4f9e-9362-8c9eedd9a2f6" readOnly
              className="border rounded-lg px-3 py-2 text-xs text-gray-400 bg-gray-50 cursor-not-allowed" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 uppercase tracking-wide">Group Role</label>
            <select name="groupRole" value={form.groupRole} onChange={handleChange}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400" required>
              <option value="">Select a group role…</option>
              {GROUP_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 uppercase tracking-wide">Location</label>
            <select name="groupLocation" value={form.groupLocation} onChange={handleChange}
              className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400" required>
              <option value="">Select a location…</option>
              {GROUP_LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>

        {(form.groupRole || form.groupLocation) && (
          <div className="bg-gray-50 border rounded-lg px-3 py-2 text-xs text-gray-600">
            <span className="font-medium">Groups will be saved as: </span>
            {JSON.stringify([form.groupRole, form.groupLocation].filter(Boolean))}
          </div>
        )}

        {status && (
          <div className={`rounded-lg px-4 py-3 text-sm ${
            status.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}>{status.message}</div>
        )}

        <button type="submit" disabled={!isValid || loading}
          className="w-full bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {loading ? "Creating user…" : "Create User"}
        </button>
      </form>

      {/* ── Manage Users ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-800">Manage Users</h3>
            <p className="text-xs text-gray-400 mt-0.5">{users.length} users · click a row to edit</p>
          </div>
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-800 w-56"
          />
        </div>

        {usersLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-800" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">Groups</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, i) => (
                  <tr
                    key={u.user_id}
                    onClick={() => setSelectedUser(u)}
                    className={`border-b border-gray-50 cursor-pointer transition-colors hover:bg-blue-50/40 ${
                      selectedUser?.user_id === u.user_id ? "bg-blue-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50/30"
                    }`}
                  >
                    <td className="px-6 py-3 font-medium text-gray-800 whitespace-nowrap">
                      {u.first_name} {u.last_name}
                    </td>
                    <td className="px-6 py-3 text-gray-500 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || "bg-gray-100 text-gray-600"}`}>
                        {ROLE_LABELS[u.role] || u.role || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block w-2 h-2 rounded-full ${u.active !== false ? "bg-green-400" : "bg-red-400"}`} />
                      <span className="ml-2 text-xs text-gray-500">{u.active !== false ? "Active" : "Disabled"}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 max-w-xs truncate">
                      {Array.isArray(u.groups) && u.groups.length > 0 ? u.groups.join(", ") : "—"}
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-400">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Side Panel ── */}
      {selectedUser && (
        <UserPanel
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSaved={handleUserSaved}
        />
      )}
    </div>
  );
}
